import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faCar,
  faClipboardList,
  faCheckCircle,
  faSearch,
  faClock,
  faRobot,
  faMoneyBillWave,
  faHistory,
  faUsers,
  faThumbsUp,
  faChartLine,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const tabs = [
  { label: "Pre-Book", icon: faCalendarCheck },
  { label: "Walk-In", icon: faCar },
  { label: "Service Required", icon: faClipboardList },
  { label: "Service Given", icon: faCheckCircle },
  { label: "Observation", icon: faSearch },
  { label: "Next Spa", icon: faClock },
  { label: "Service AI", icon: faRobot },
  { label: "Payment Log", icon: faMoneyBillWave },
  { label: "My Spa History", icon: faHistory },
  { label: "Customer History", icon: faUsers },
  { label: "Thank You", icon: faThumbsUp },
  { label: "Customer Trend", icon: faChartLine },
  { label: "Business Amount", icon: faChartBar },
];

export default function CarSpaHome() {
  const [activeTab, setActiveTab] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    setSearchTerm("");
    setFilterStatus("All");
  }, [activeTab]);

  // Chart data (realistic dummy seed data)
  const customerTrendData = {
    labels: ["Aug 2025", "Sep", "Oct", "Nov", "Dec", "Jan 2026"],
    datasets: [
      {
        label: "Total Customers",
        data: [118, 142, 138, 165, 158, 182],
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        tension: 0.3,
      },
      {
        label: "Repeat Customers",
        data: [72, 88, 92, 110, 105, 128],
        borderColor: "rgb(16, 185, 129)",
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        tension: 0.3,
      },
    ],
  };

  const businessAmountData = {
    labels: ["Aug 2025", "Sep", "Oct", "Nov", "Dec", "Jan 2026"],
    datasets: [
      {
        label: "Revenue (₹)",
        data: [118000, 142000, 138500, 165000, 158000, 182500],
        backgroundColor: "rgba(75, 192, 192, 0.7)",
        borderColor: "rgb(75, 192, 192)",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      tooltip: { mode: "index", intersect: false },
    },
  };

  // Dummy data for tabs
  const preBookData = [
    { id: 1, customer: "Vikram Patel", vehicle: "Honda City GJ05 CD3456", dateTime: "2026-01-04 10:00", services: "Premium Wash + Wax", status: "Confirmed" },
    { id: 2, customer: "Priya Mehta", vehicle: "Hyundai Creta MH02 KL7890", dateTime: "2026-01-04 14:00", services: "Full Interior Detailing", status: "Confirmed" },
    { id: 3, customer: "Arjun Singh", vehicle: "Toyota Innova KA03 AB1122", dateTime: "2026-01-05 09:30", services: "Ceramic Coating", status: "Pending" },
    { id: 4, customer: "Neha Gupta", vehicle: "Maruti Swift DL10 XY5566", dateTime: "2026-01-05 12:00", services: "Basic Wash", status: "Confirmed" },
    { id: 5, customer: "Rohan Desai", vehicle: "Ford EcoSport TN01 ZZ9999", dateTime: "2026-01-06 11:00", services: "Polish + Interior", status: "Confirmed" },
    { id: 6, customer: "Sonia Reddy", vehicle: "Kia Seltos AP09 EF4343", dateTime: "2026-01-07 15:30", services: "Full Detailing", status: "Confirmed" },
  ];

  const walkInData = [
    { id: 1, customer: "Karan Malhotra", vehicle: "Mahindra XUV700 MH14 GH2233", arrival: "09:15 AM", services: "Premium Wash", status: "In Progress" },
    { id: 2, customer: "Anita Joshi", vehicle: "Tata Nexon UP16 JK8899", arrival: "10:30 AM", services: "Interior Cleaning", status: "Waiting" },
    { id: 3, customer: "Sameer Khan", vehicle: "Volkswagen Polo DL5C MN4455", arrival: "11:00 AM", services: "Quick Wash", status: "In Progress" },
    { id: 4, customer: "Deepa Nair", vehicle: "Skoda Kushaq KA51 PQ6677", arrival: "11:45 AM", services: "Wax + Vacuum", status: "Waiting" },
  ];

  const serviceRequiredData = [
    { vehicle: "Mahindra XUV700 MH14 GH2233", customer: "Karan Malhotra", required: "Exterior Wash, Wax, Tire Shine" },
    { vehicle: "Tata Nexon UP16 JK8899", customer: "Anita Joshi", required: "Full Interior Vacuum, Dashboard Polish" },
    { vehicle: "Volkswagen Polo DL5C MN4455", customer: "Sameer Khan", required: "Basic Exterior Wash" },
  ];

  const serviceGivenData = [
    { vehicle: "Hyundai Verna HR26 TQ1122", customer: "Ravi Verma", services: "Full Detailing + Ceramic", completed: "10:30 AM", amount: 8000 },
    { vehicle: "Maruti Baleno PB10 UV7788", customer: "Simran Kaur", services: "Premium Wash + Interior", completed: "11:15 AM", amount: 3500 },
    { vehicle: "Honda WR-V GJ01 LM3344", customer: "Mahesh Shah", services: "Quick Wash", completed: "12:00 PM", amount: 1200 },
  ];

  const observationData = [
    { vehicle: "Mahindra XUV700 MH14 GH2233", customer: "Karan Malhotra", observation: "Minor scratch on left rear door", notedBy: "Technician Raj" },
    { vehicle: "Tata Nexon UP16 JK8899", customer: "Anita Joshi", observation: "Seat stains require deep cleaning", notedBy: "Technician Anil" },
    { vehicle: "Volkswagen Polo DL5C MN4455", customer: "Sameer Khan", observation: "Headlights slightly foggy - suggest polishing", notedBy: "Technician Raj" },
  ];

  const nextSpaData = [
    { customer: "Vikram Patel", vehicle: "Honda City GJ05 CD3456", lastSpa: "2025-12-10", nextRecommended: "2026-03-10 (3 months)" },
    { customer: "Priya Mehta", vehicle: "Hyundai Creta MH02 KL7890", lastSpa: "2025-11-20", nextRecommended: "2026-02-20 (3 months)" },
    { customer: "Ravi Verma", vehicle: "Hyundai Verna HR26 TQ1122", lastSpa: "2026-01-02", nextRecommended: "2026-04-02 (3 months)" },
  ];

  const paymentData = [
    { date: "2026-01-02", customer: "Ravi Verma", vehicle: "Hyundai Verna HR26 TQ1122", amount: 8000, method: "UPI", status: "Paid" },
    { date: "2026-01-02", customer: "Simran Kaur", vehicle: "Maruti Baleno PB10 UV7788", amount: 3500, method: "Card", status: "Paid" },
    { date: "2026-01-01", customer: "Amit Sharma", vehicle: "Toyota Camry DL9C AB1234", amount: 4500, method: "Cash", status: "Paid" },
    { date: "2025-12-31", customer: "Neha Gupta", vehicle: "Maruti Swift DL10 XY5566", amount: 1800, method: "UPI", status: "Paid" },
    { date: "2025-12-30", customer: "Rohan Desai", vehicle: "Ford EcoSport TN01 ZZ9999", amount: 6000, method: "Card", status: "Paid" },
  ];

  const spaHistoryData = [
    { date: "2026-01-02", customer: "Ravi Verma", vehicle: "Hyundai Verna HR26 TQ1122", services: "Full Detailing + Ceramic", amount: 8000 },
    { date: "2026-01-02", customer: "Simran Kaur", vehicle: "Maruti Baleno PB10 UV7788", services: "Premium Wash + Interior", amount: 3500 },
    { date: "2026-01-01", customer: "Karan Malhotra", vehicle: "Mahindra XUV700 MH14 GH2233", services: "Premium Wash", amount: 2500 },
    { date: "2025-12-30", customer: "Priya Mehta", vehicle: "Hyundai Creta MH02 KL7890", services: "Interior Detailing", amount: 4000 },
    { date: "2025-12-28", customer: "Vikram Patel", vehicle: "Honda City GJ05 CD3456", services: "Wax + Polish", amount: 3000 },
    { date: "2025-12-25", customer: "Arjun Singh", vehicle: "Toyota Innova KA03 AB1122", services: "Full Spa", amount: 7500 },
  ];

  return (
    <div className="min-h-screen bg-black text-blue-400 p-3">
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="text-xl font-bold text-blue-500">🚗 CarSpa Dashboard</h1>
        <p className="text-xs text-gray-400">
          Smart Vehicle Spa & Detailing Management
        </p>
      </div>

      {/* Grid Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(tab.label)}
            className={`w-full h-24 rounded-xl flex flex-col items-center justify-center text-sm font-medium transition ${
              activeTab === tab.label
                ? "bg-blue-900 border-2 border-blue-500"
                : "bg-zinc-900 hover:bg-zinc-800"
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="text-lg mb-1" />
            <span className="text-center leading-tight">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="mt-6">
        {activeTab === null ? (
          <>
            {/* Info Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <div className="bg-zinc-900 p-4 rounded-xl">
                <h2 className="text-sm font-semibold mb-2 text-blue-500">
                  Today’s Summary (Jan 02, 2026)
                </h2>
                <ul className="text-xs space-y-1 text-gray-300">
                  <li>🚘 Vehicles Serviced: 18</li>
                  <li>🧼 Premium Wash: 7</li>
                  <li>✨ Interior Detailing: 5</li>
                  <li>💰 Revenue: ₹14,500</li>
                  <li>⏱ Avg Time / Car: 52 mins</li>
                </ul>
              </div>

              <div className="bg-zinc-900 p-4 rounded-xl">
                <h2 className="text-sm font-semibold mb-2 text-blue-500">
                  Service AI Insights 🤖
                </h2>
                <ul className="text-xs space-y-1 text-gray-300">
                  <li>📈 SUV customers prefer monthly spa</li>
                  <li>🧴 Add ceramic coating upsell (high margin)</li>
                  <li>🕘 Peak walk-ins: 11am – 2pm</li>
                  <li>💡 Suggest AMC for 15+ repeat customers</li>
                  <li>⚡ 68% payments via UPI today</li>
                </ul>
              </div>
            </div>

            {/* Graphs on Dashboard */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <div className="bg-zinc-900 p-4 rounded-xl">
                <h2 className="text-sm font-semibold text-blue-500 mb-4">
                  Customer Trend
                </h2>
                <div className="h-64">
                  <Line data={customerTrendData} options={chartOptions} />
                </div>
              </div>

              <div className="bg-zinc-900 p-4 rounded-xl">
                <h2 className="text-sm font-semibold text-blue-500 mb-4">
                  Business Revenue
                </h2>
                <div className="h-64">
                  <Bar data={businessAmountData} options={chartOptions} />
                </div>
              </div>
            </div>

            {/* Footer Message */}
            <div className="bg-zinc-900 p-4 rounded-xl mt-6 text-center">
              <h3 className="text-sm font-semibold text-blue-500">
                Thank You for Your Trust 🙏
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                We value your trust. Your car deserves the best care!
              </p>
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => setActiveTab(null)}
              className="mb-4 text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
            >
              ← Back to Dashboard
            </button>
            <h2 className="text-xl font-bold text-blue-500 mb-6">{activeTab}</h2>

            {/* Tab-specific content */}
            {activeTab === "Pre-Book" && (
              <div>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Search customer / vehicle..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 bg-zinc-800 p-3 rounded-lg text-xs text-white"
                  />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-zinc-800 p-3 rounded-lg text-xs text-white"
                  >
                    <option>All</option>
                    <option>Confirmed</option>
                    <option>Pending</option>
                  </select>
                </div>
                <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-800">
                      <tr>
                        <th className="p-3 text-left">Customer</th>
                        <th className="p-3 text-left">Vehicle</th>
                        <th className="p-3 text-left">Date & Time</th>
                        <th className="p-3 text-left">Services</th>
                        <th className="p-3 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preBookData
                        .filter((item) => filterStatus === "All" || item.status === filterStatus)
                        .filter(
                          (item) =>
                            searchTerm === "" ||
                            item.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.vehicle.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((item) => (
                          <tr key={item.id} className="border-t border-zinc-800 hover:bg-zinc-800">
                            <td className="p-3">{item.customer}</td>
                            <td className="p-3">{item.vehicle}</td>
                            <td className="p-3">{item.dateTime}</td>
                            <td className="p-3">{item.services}</td>
                            <td className="p-3">
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  item.status === "Confirmed" ? "bg-green-900" : "bg-yellow-900"
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Walk-In" && (
              <div>
                <input
                  type="text"
                  placeholder="Search customer / vehicle..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full mb-4 bg-zinc-800 p-3 rounded-lg text-xs text-white"
                />
                <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-800">
                      <tr>
                        <th className="p-3 text-left">Customer</th>
                        <th className="p-3 text-left">Vehicle</th>
                        <th className="p-3 text-left">Arrival</th>
                        <th className="p-3 text-left">Services</th>
                        <th className="p-3 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walkInData
                        .filter(
                          (item) =>
                            searchTerm === "" ||
                            item.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.vehicle.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((item) => (
                          <tr key={item.id} className="border-t border-zinc-800 hover:bg-zinc-800">
                            <td className="p-3">{item.customer}</td>
                            <td className="p-3">{item.vehicle}</td>
                            <td className="p-3">{item.arrival}</td>
                            <td className="p-3">{item.services}</td>
                            <td className="p-3">
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  item.status === "In Progress" ? "bg-blue-900" : "bg-orange-900"
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Service Required" && (
              <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="p-3 text-left">Vehicle</th>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-left">Required Services</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceRequiredData.map((item, i) => (
                      <tr key={i} className="border-t border-zinc-800">
                        <td className="p-3">{item.vehicle}</td>
                        <td className="p-3">{item.customer}</td>
                        <td className="p-3">{item.required}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "Service Given" && (
              <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="p-3 text-left">Vehicle</th>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-left">Services Given</th>
                      <th className="p-3 text-left">Completed</th>
                      <th className="p-3 text-left">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceGivenData.map((item, i) => (
                      <tr key={i} className="border-t border-zinc-800">
                        <td className="p-3">{item.vehicle}</td>
                        <td className="p-3">{item.customer}</td>
                        <td className="p-3">{item.services}</td>
                        <td className="p-3">{item.completed}</td>
                        <td className="p-3">₹{item.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "Observation" && (
              <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="p-3 text-left">Vehicle</th>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-left">Observation</th>
                      <th className="p-3 text-left">Noted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {observationData.map((item, i) => (
                      <tr key={i} className="border-t border-zinc-800">
                        <td className="p-3">{item.vehicle}</td>
                        <td className="p-3">{item.customer}</td>
                        <td className="p-3">{item.observation}</td>
                        <td className="p-3">{item.notedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "Next Spa" && (
              <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-800">
                    <tr>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-left">Vehicle</th>
                      <th className="p-3 text-left">Last Spa</th>
                      <th className="p-3 text-left">Next Recommended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nextSpaData.map((item, i) => (
                      <tr key={i} className="border-t border-zinc-800">
                        <td className="p-3">{item.customer}</td>
                        <td className="p-3">{item.vehicle}</td>
                        <td className="p-3">{item.lastSpa}</td>
                        <td className="p-3">{item.nextRecommended}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "Service AI" && (
              <div className="bg-zinc-900 p-6 rounded-xl">
                <ul className="space-y-4 text-sm text-gray-300">
                  <li className="bg-zinc-800 p-4 rounded-lg">• SUV segment shows 42% higher spend on premium services – recommend ceramic upsell</li>
                  <li className="bg-zinc-800 p-4 rounded-lg">• 68% of repeat customers return within 90 days – push Annual Maintenance Contract</li>
                  <li className="bg-zinc-800 p-4 rounded-lg">• Peak hours 11 AM – 2 PM: consider adding one more bay on weekends</li>
                  <li className="bg-zinc-800 p-4 rounded-lg">• Interior detailing margin highest at 58% – promote during rainy season</li>
                  <li className="bg-zinc-800 p-4 rounded-lg">• 12 customers overdue for next spa – send reminder campaign</li>
                </ul>
              </div>
            )}

            {activeTab === "Payment Log" && (
              <div>
                <input
                  type="text"
                  placeholder="Search customer / vehicle..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full mb-4 bg-zinc-800 p-3 rounded-lg text-xs text-white"
                />
                <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-800">
                      <tr>
                        <th className="p-3 text-left">Date</th>
                        <th className="p-3 text-left">Customer</th>
                        <th className="p-3 text-left">Vehicle</th>
                        <th className="p-3 text-left">Amount</th>
                        <th className="p-3 text-left">Method</th>
                        <th className="p-3 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentData
                        .filter(
                          (item) =>
                            searchTerm === "" ||
                            item.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.vehicle.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((item, i) => (
                          <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-800">
                            <td className="p-3">{item.date}</td>
                            <td className="p-3">{item.customer}</td>
                            <td className="p-3">{item.vehicle}</td>
                            <td className="p-3">₹{item.amount}</td>
                            <td className="p-3">{item.method}</td>
                            <td className="p-3">
                              <span className="px-2 py-1 rounded-full text-xs bg-green-900">
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "My Spa History" && (
              <div>
                <input
                  type="text"
                  placeholder="Search by date / customer / services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full mb-4 bg-zinc-800 p-3 rounded-lg text-xs text-white"
                />
                <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-800">
                      <tr>
                        <th className="p-3 text-left">Date</th>
                        <th className="p-3 text-left">Customer</th>
                        <th className="p-3 text-left">Vehicle</th>
                        <th className="p-3 text-left">Services</th>
                        <th className="p-3 text-left">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spaHistoryData
                        .filter(
                          (item) =>
                            searchTerm === "" ||
                            item.date.includes(searchTerm) ||
                            item.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.services.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((item, i) => (
                          <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-800">
                            <td className="p-3">{item.date}</td>
                            <td className="p-3">{item.customer}</td>
                            <td className="p-3">{item.vehicle}</td>
                            <td className="p-3">{item.services}</td>
                            <td className="p-3">₹{item.amount}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Customer History" && (
              <div>
                <input
                  type="text"
                  placeholder="Search customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full mb-4 bg-zinc-800 p-3 rounded-lg text-xs text-white"
                />
                <div className="bg-zinc-900 rounded-xl overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-800">
                      <tr>
                        <th className="p-3 text-left">Date</th>
                        <th className="p-3 text-left">Customer</th>
                        <th className="p-3 text-left">Vehicle</th>
                        <th className="p-3 text-left">Services</th>
                        <th className="p-3 text-left">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spaHistoryData
                        .filter((item) => 
                          searchTerm === "" ||
                          item.customer.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((item, i) => (
                          <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-800">
                            <td className="p-3">{item.date}</td>
                            <td className="p-3">{item.customer}</td>
                            <td className="p-3">{item.vehicle}</td>
                            <td className="p-3">{item.services}</td>
                            <td className="p-3">₹{item.amount}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "Thank You" && (
              <div className="text-center py-12">
                <h3 className="text-2xl font-bold text-blue-500 mb-6">
                  Thank You for Choosing CarSpa! 🙏
                </h3>
                <p className="text-gray-300 mb-8 max-w-lg mx-auto">
                  We appreciate your trust in us. Here are today's happy customers:
                </p>
                <ul className="space-y-3 text-left max-w-md mx-auto">
                  <li className="bg-zinc-900 p-4 rounded-lg">Ravi Verma – Hyundai Verna</li>
                  <li className="bg-zinc-900 p-4 rounded-lg">Simran Kaur – Maruti Baleno</li>
                  <li className="bg-zinc-900 p-4 rounded-lg">Karan Malhotra – Mahindra XUV700</li>
                  <li className="bg-zinc-900 p-4 rounded-lg">Anita Joshi – Tata Nexon</li>
                </ul>
                <p className="text-gray-400 mt-8 text-sm">
                  Your car is shining brighter because of you!
                </p>
              </div>
            )}

            {activeTab === "Customer Trend" && (
              <div className="bg-zinc-900 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-blue-500 mb-6">
                  Customer Trend (Last 6 Months)
                </h3>
                <div className="h-96">
                  <Line data={customerTrendData} options={chartOptions} />
                </div>
              </div>
            )}

            {activeTab === "Business Amount" && (
              <div className="bg-zinc-900 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-blue-500 mb-6">
                  Monthly Revenue (Last 6 Months)
                </h3>
                <div className="h-96">
                  <Bar data={businessAmountData} options={chartOptions} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}